import React, { useLayoutEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Container, Row } from "reactstrap";
import { getSectors } from "../../redux/homepage/saga";
import { SectorDto } from "../../redux/homepage/type";

import { Helmet } from "react-helmet";
import { appUrl } from "../../redux/base/types/url";
import { getLocalization } from "../../redux/localization";
import { useLocation } from "react-router-dom";
import SectorCard from "./SectorCard";
import { languageSelector, languagesSelector } from "../../redux/languages";
import { isLoading } from "../../redux/loader";
import { Loader } from "../../components";
import images from "../../assets/images";

const SectorsPage = () => {
  const dispatch = useDispatch();
  const languages = useSelector(languagesSelector);
  const language = useSelector(languageSelector);
  const location = useLocation();
  const loading = useSelector(isLoading);
  const [google, setGoogle] = useState<string>();
  const [sectors, setSectors] = useState<SectorDto[]>([]);
  const t = useSelector(getLocalization);

  useLayoutEffect(() => {
    dispatch(
      getSectors({
        payload: {
          onSuccess: async (message, payload, details) => {
            setSectors(payload);
            details && setGoogle(details);
          },
          onError: () => {},
        },
      })
    );
  }, [dispatch, language]);

  const title = t.getString("SectorsSeoTitle");
  const desc = t.getString("SectorsSeoDescription");

  return (
    <React.Fragment>
      {loading && <Loader />}
      <Helmet>
       
      <title>Rental Software Solutions by Industry | Rentificial</title>
<meta name="description" content="Explore Rentificial’s smart rental software solutions tailored for cars, bikes, scooters, caravans, and equipment rental businesses." />
<meta name="keywords" content="rental software solutions, car rental software, bike rental software, scooter rental platform, equipment rental software, Rentificial sectors" />
<link rel="canonical" href="https://www.rentificial.com/solutions/" />

        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={desc} />
        <meta name="twitter:image" content={images.sectorimg} />
        <meta name="twitter:card" content={"large_summary_card"} />
        <meta name="twitter:site" content="@rentificial" />
        <meta name="twitter:creator" content="@rentificial" />
        <meta property="og:title" content={title} />
        <meta property="og:type" content="website" />
        <meta property="og:description" content={desc} />
        <meta property="og:image" content={images.sectorimg} />
        <meta property="og:url" content={appUrl + location.pathname} />

        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Homepage",
                  "item": "${appUrl}"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "${t.getString("SectorsTitle")}",
                  "item": "${appUrl + "/solutions"}"
                }
              ]
            }`}
        </script>
        {google && (
          <script type="application/ld+json">
            {`${JSON.stringify(google)}`}
          </script>
        )}
      </Helmet>

      <div className="spesificationPage">
        <Container className="py-5">
          <div className="text-center">
            <h1>{t.getString("Sectors PageTitle")}</h1>
            <h2 style={{ fontSize: 16 }}>{t.getString("Sectors PageContent")}</h2>
          </div>
          <Row className="mt-4">
            {sectors.map((x) => (
              <Col md={4} key={x.Id} className="mb-3">
                <SectorCard sector={x} />
              </Col>
            ))}
          </Row>
          <Row className="mt-5">
          <Col md={12} className="text-left">
  <div style={{ backgroundColor: '#f8f9fa', padding: '30px', borderRadius: '8px' }}>
    <h2 style={{ fontSize: '24px', fontWeight: 'bold' }}>🚗 Smart Solutions for Every Rental Business</h2>
    <p>
      Whether you're managing a small urban fleet or operating a large-scale vehicle rental company with hundreds of units, Rentificial provides scalable and intelligent <strong>car rental software solutions</strong> that adapt to your unique workflow. Our platform is designed to support the full rental lifecycle, helping businesses reduce manual operations, boost customer satisfaction, and increase profitability.
    </p>
    <p>
      From online bookings to financial reporting, everything you need is available in one centralized cloud-based dashboard — accessible from anywhere and built to support teams of all sizes. You don’t need any technical expertise to get started — our interface is intuitive, and our setup is designed for simplicity and speed.
    </p>

    <h3 style={{ fontSize: '20px', marginTop: '20px' }}>📊 All-in-One Car Rental Software</h3>
    <ul>
      <li>Accept reservations online and through mobile devices</li>
      <li>Track real-time vehicle availability with automatic sync</li>
      <li>Manage short-term, long-term, and corporate contracts</li>
      <li>Automate pricing dynamically based on seasonality and demand</li>
      <li>Create professional invoices and generate real-time financial reports</li>
      <li>Assign vehicles intelligently to reduce idle time</li>
    </ul>

    <h3 style={{ fontSize: '20px', marginTop: '20px' }}>🔐 Easy Access with Secure Login</h3>
    <p>
      Team collaboration is simple and secure with Rentificial. Our secure <strong>car rental solutions login</strong> gives every user personalized access with role-based permissions. Whether you have front desk agents, branch managers, or financial officers, each user sees exactly what they need — no more, no less.
    </p>

    <h3 style={{ fontSize: '20px', marginTop: '20px' }}>🌐 White-Labeled Web Access</h3>
    <p>
      Want to impress your customers? Every Rentificial solution includes a white-labeled, responsive website where your users can browse cars, view availability, and make bookings instantly. It's your brand, your experience — powered by our technology.
    </p>

    <h3 style={{ fontSize: '20px', marginTop: '20px' }}>📈 Analytics & Growth</h3>
    <p>
      Make smarter decisions with built-in analytics. From vehicle utilization rates to revenue breakdowns and customer behavior insights, our dashboards help you spot trends, track performance, and plan for growth. Whether you're scaling up or fine-tuning, Rentificial helps turn data into action.
    </p>
  </div>
</Col>

          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default SectorsPage;
